/**
 * Contains the logic for the audio meditations
 *
 * @fileoverview
 */

import { err, ok, Result, ResultAsync } from "neverthrow";
import { AUDIO_FORMAT_EXTENSION, playFile } from "./audiobook";
import type { PracticeAny } from "@circleofa/acim-engine";
import { LessonTimeExactMinute, timeCrunchMachine } from "@utils/practice/reminder-time-conversion";

/** Root of audio meditations */
const ORIGIN = "https://audio.acimce.app/meditations/";

/** Index list of the files in the root directory */
export const LIST_URL = `${ORIGIN}/_list.txt`;

/** Locally cached meditation list class */
let meditationList: ResultAsync<MeditationList, unknown> | undefined = undefined;

export enum MeditationAudioType {
  frequent = "frequent",
  hourly = "hourly",
  halfHourly = "halfHourly",
  /** Response to temptation */
  response = "response",
  main = "main",
  morningEvening = "morningEvening",
}

export function getMeditationAudioType(practice : PracticeAny) : Result<MeditationAudioType, Error> {
  switch (practice.type) {
    case "Frequent Reminders": return ok(MeditationAudioType.frequent);
    case "Main Practice": return ok(MeditationAudioType.main);
    case "Practice": return ok(MeditationAudioType.main);
    case "Response to Temptation": return ok(MeditationAudioType.response);
    case "Morning and Evening Practice": return ok(MeditationAudioType.morningEvening);
    case "Hourly Remembrance": {
      const crunched = timeCrunchMachine(practice.time!) as LessonTimeExactMinute
      if (crunched.descriptionOfMinutes === "half")
        return ok(MeditationAudioType.halfHourly);
      if (crunched.descriptionOfMinutes === "hour")
        return ok(MeditationAudioType.hourly);
    }
  }
  return err(new Error("Unknown meditation type"));
}

/** MeditationItem is a class that represents a meditation audio item */
export class MeditationItem {
  constructor(
    /** Original path of the meditatio audio (e.g. `232-frequent-01`) */
    public readonly path : string,
    /** Lesson number associated with the meditation (e.g. 232) */
    public readonly lesson : number,
    /** Type of meditation audio (e.g. frequent) */
    public readonly type : MeditationAudioType,
    /** Whether the meditation is an alternative version */
    public readonly alt : boolean,
    /** Whether the meditation is the morning version */
    public readonly morning : boolean | undefined) {}

  /** Process a filename to create a MeditationItem. If invalid, returns null */
  static process(filename : string) {
    filename = filename.split(".")[0] // Remove extension
    const [lesson, typeString] = filename.split(/-(.*)/s).map(s => s.trim());
    if (!lesson || !typeString) {
      console.warn(`Invalid filename: ${filename}`);
      return null;
    }
    // Remove everything non-alpha
    let sanitisedType = typeString.toLowerCase().replace(/[^a-z]/g, '');

    // Check and remove "alt" for alternative meditations
    const alt = (sanitisedType.includes("alt"))
    sanitisedType = sanitisedType.replace("alt", "");
    let morning : boolean | undefined = undefined
    let type : MeditationAudioType = MeditationAudioType.main;
    if (sanitisedType === "evening") {
      type = MeditationAudioType.morningEvening;
      morning = false
    } else if (sanitisedType === "morning") {
      type = MeditationAudioType.morningEvening;
      morning = true
    } else if (sanitisedType === "frequenthalfhourly" || sanitisedType === "halfhourly") {
      type = MeditationAudioType.halfHourly;
    } else if (sanitisedType === "frequenthourly" || sanitisedType === "hourly" || sanitisedType === "hourlyremembrance") {
      type = MeditationAudioType.hourly;
    } else if (sanitisedType === "frequent") {
      type = MeditationAudioType.frequent;
    } else if (sanitisedType === "response" || sanitisedType === "temptation") {
      type = MeditationAudioType.response;
    } else if (sanitisedType === "") {
      // Do nothing - already set to main
    } else {
      console.error("Failed to parse type: " + sanitisedType + " with filename: " + filename);
    }
    return new MeditationItem(filename, parseInt(lesson), type, alt, morning);
  }

  toString() {
    return `${this.lesson} | ${this.path}`;
  }

  /** Get actual HTTP url of meditation */
  get url() {
    return ORIGIN + this.path + "." + AUDIO_FORMAT_EXTENSION;
  }

  get title() {
    const morning = this.morning === undefined ? "" : this.morning ? "Morning" : "Evening";
    return `${morning} Meditation ${this.alt ? "(alt)" : ""}`.trim();
  }

  /**
   * Plays the meditation audio file with the provided information.
   * @param info
   */
  async play(practice : PracticeAny) {
    await playFile(this.url, {
      prefix: this.title, // Top faded
      title: practice.type // Main title
    });
  }
}

/**
 * Structure that allows for easy access to meditation data
 */
class MeditationList {
  items : MeditationItem[];
  constructor(list : string[]) {
    this.items = list.map(MeditationItem.process).filter(item => item !== null);
  }

  /**
   * Retrieves meditations for a specific lesson
   * @param lesson
   * @returns
   */
  forLesson(lesson : number) {
    return this.items.filter(item => item.lesson === lesson);
  }
}

/**
 * Retrieves the cached meditation list instance
 * @returns
 */
export const getMeditationListInstance = () => {
  if (meditationList) return meditationList;
  const promise = fetch(LIST_URL)
    .then(res => res.ok ? res : Promise.reject(res))
    .then(res => res.text())
    .then(text => text.split("\n"))
    .then(list => new MeditationList(list))
  meditationList = ResultAsync.fromPromise(promise, error => error);
  return meditationList
}

let rechecking = false
window.addEventListener("online", async () => {
  // Hasn't even been requested yet
  if (!meditationList) return
  if (rechecking) return
  console.log("Online event triggered - Rechecking for meditation practice API...");
  const list = await meditationList
  if (list.isErr()) {
    meditationList = undefined
    await getMeditationListInstance()
  }
  rechecking = false
})
